import React, { useEffect, useMemo, useRef, useState } from "react";
import { isEqual, format } from "date-fns";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Alert, FormControlLabel, Switch, useTheme } from "@mui/material";
import { useListAndSearchAllDividends } from "@rentablo/api/apiv1";
import { Dividend } from "@rentablo/api/apiv1";
import { TransactionValidationError } from "@rentablo/api/apiv1";
import { DividendWizardContext } from "@features/dividendWizard/stateMachine/types/dividendWizardContext.ts";
import { StepContainer } from "../../transactionWizard/StepContainer";
import { DividendsTable } from "../components/DividendsTable";

export interface Props {
  context: DividendWizardContext;
  previouslySelectedDividends: Dividend[];
  validationErrors: TransactionValidationError[];
  onNext: (selectedDividends: Dividend[]) => void;
}

const ValidationErrorsContainer = styled(Alert)`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

export const ListDividendsStep = (props: Props) => {
  const {
    context,
    onNext,
    previouslySelectedDividends = [],
    validationErrors,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const [splitAdjust, setSplitAdjust] = useState(context.splitAdjust);
  const { data: dividends, isFetching } = useListAndSearchAllDividends({
    investmentId: [context.investment?.id!],
    excludeImported: false,
    splitAdjust,
  });
  const data = dividends?.dividends?.filter(
    ({ numberOfLots = 0 }) => numberOfLots > 0
  );

  const [selectedDividends, setSelectedDividends] = useState<Dividend[]>([]);

  const unreceivedDividends = useMemo(
    () => data?.filter(({ bookingId }) => !bookingId) ?? [],
    [data]
  );

  const handleNext = () => {
    if (!selectedDividends.length) return null;

    return onNext(selectedDividends);
  };

  const handleSelectDividend = (selectedDividend: Dividend) => {
    const selectedIndex = selectedDividends.findIndex(({ date }) => {
      if (!date || !selectedDividend.date) return false;

      return isEqual(new Date(date), new Date(selectedDividend.date));
    });
    let newSelected: Dividend[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedDividends, selectedDividend);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedDividends.slice(1));
    } else if (selectedIndex === selectedDividends.length - 1) {
      newSelected = newSelected.concat(selectedDividends.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedDividends.slice(0, selectedIndex),
        selectedDividends.slice(selectedIndex + 1)
      );
    }

    setSelectedDividends(newSelected);
  };

  const handleSelectAllDividends = (type: "select" | "unselect") => {
    if (type === "select") {
      setSelectedDividends(unreceivedDividends);
      return;
    }
    setSelectedDividends([]);
  };

  // Only on the first load, so a refetch can't refill a selection the user
  // cleared.
  const isSelectionInitialized = useRef(false);
  useEffect(() => {
    if (isFetching || !dividends || isSelectionInitialized.current) return;
    isSelectionInitialized.current = true;
    setSelectedDividends(
      previouslySelectedDividends.length
        ? previouslySelectedDividends
        : unreceivedDividends
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  return (
    <StepContainer
      testid="select-dividend-step"
      title={t("batchDividendsWizard.listDividendsStep.title")}
      subtitle={t("batchDividendsWizard.listDividendsStep.subtitle")}
      onNext={handleNext}
      onNextDisabled={!selectedDividends.length}
    >
      {Boolean(validationErrors?.length) &&
        validationErrors.map(({ errorMessage, date }) => (
          <ValidationErrorsContainer
            severity="error"
            data-testid="dividend-validation-error"
            key={errorMessage}
          >
            {format(new Date(date!), "dd.MM.yyyy")} - {errorMessage}
          </ValidationErrorsContainer>
        ))}
      <FormControlLabel
        control={
          <Switch
            checked={splitAdjust}
            value={splitAdjust}
            onChange={() => setSplitAdjust((prev) => !prev)}
          />
        }
        label={t("batchDividendsWizard.listDividendsStep.splitAdjust")}
        sx={{
          margin: theme.spacing(1, 0, 4, 0),
        }}
      />
      <DividendsTable
        allDividends={data ?? []}
        selectedDividends={selectedDividends}
        unreceivedDividendsCount={unreceivedDividends.length}
        onSelectAllDividends={handleSelectAllDividends}
        onSelectDividend={handleSelectDividend}
      />
    </StepContainer>
  );
};
